import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Building2, ChevronRight, Folder, FolderTree, List, Settings } from 'lucide-react';
import { cn } from '../../utils/cn';
import { apiService } from '../../services/api';
import { useRBAC } from '../../contexts/RBACContext';

interface ClickUpTreeNode {
  id: string;
  name: string;
  type: 'workspace' | 'space' | 'folder' | 'list';
  color?: string;
  spaces?: ClickUpTreeNode[];
  folders?: ClickUpTreeNode[];
  lists?: ClickUpTreeNode[];
}

const NODE_ICONS: Record<ClickUpTreeNode['type'], React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  workspace: Building2,
  space: FolderTree,
  folder: Folder,
  list: List,
};

const childrenOf = (node: ClickUpTreeNode): ClickUpTreeNode[] => [
  ...(node.spaces ?? []),
  ...(node.folders ?? []),
  ...(node.lists ?? []),
];

/** The /clickup/tree endpoint has historically answered in several envelope shapes. */
const unwrapTree = (response: any): ClickUpTreeNode[] => {
  if (response?.success && Array.isArray(response.results)) return response.results;
  if (response?.result?.success && Array.isArray(response.result.results)) return response.result.results;
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.result)) return response.result;
  console.warn('Unexpected ClickUp tree response structure:', response);
  return [];
};

/** Ids of the ancestors of the node identified by (type, id), or null when absent. */
const findAncestorIds = (
  nodes: ClickUpTreeNode[],
  targetType: string,
  targetId: string,
  path: string[] = [],
): string[] | null => {
  for (const node of nodes) {
    if (node.id === targetId && node.type === targetType) return path;
    const found = findAncestorIds(childrenOf(node), targetType, targetId, [...path, node.id]);
    if (found) return found;
  }
  return null;
};

const CLICKUP_TASKS_ROUTE = /\/clickup\/tasks\/(list|folder|space)\/([^/]+)/;

/**
 * Expandable ClickUp workspace tree shown in the sidebar's Projects group.
 * Visibility (imports:read) is decided by the Sidebar; this component only
 * owns fetching and expansion state.
 */
const ClickUpNavTree: React.FC = () => {
  const { isLoading: permissionsLoading, hasPermission } = useRBAC();
  const location = useLocation();
  const navigate = useNavigate();

  const isClickUpRoute = location.pathname.startsWith('/clickup/');
  const [open, setOpen] = useState(isClickUpRoute);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [tree, setTree] = useState<ClickUpTreeNode[]>([]);
  const [loading, setLoading] = useState(false);

  const canReadTree = !permissionsLoading && hasPermission('imports', 'read');

  useEffect(() => {
    if (!canReadTree) return;
    let cancelled = false;
    setLoading(true);
    apiService
      .get('/clickup/tree')
      .then(response => { if (!cancelled) setTree(unwrapTree(response)); })
      .catch(error => {
        console.error('Failed to fetch ClickUp tree:', error);
        if (!cancelled) setTree([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [canReadTree]);

  // Opening a ClickUp task page reveals the tree and the path to the active node.
  useEffect(() => {
    if (!isClickUpRoute) return;
    setOpen(true);
    const match = location.pathname.match(CLICKUP_TASKS_ROUTE);
    if (!match || tree.length === 0) return;
    const ancestors = findAncestorIds(tree, match[1], match[2]);
    if (ancestors?.length) {
      setExpandedNodes(prev => new Set([...prev, ...ancestors]));
    }
  }, [isClickUpRoute, location.pathname, tree]);

  const toggleNode = useCallback((nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const renderNode = (node: ClickUpTreeNode, depth: number): React.ReactNode => {
    const children = childrenOf(node);
    const hasChildren = children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isNavigable = node.type !== 'workspace';
    const isActive = location.pathname.includes(`/clickup/tasks/${node.type}/${node.id}`);
    const Icon = NODE_ICONS[node.type] ?? FolderTree;

    const handleClick = () => {
      if (hasChildren) toggleNode(node.id);
      if (isNavigable) navigate(`/clickup/tasks/${node.type}/${node.id}`);
    };

    return (
      <li key={node.id}>
        <button
          type="button"
          onClick={handleClick}
          aria-expanded={hasChildren ? isExpanded : undefined}
          className={cn(
            'flex w-full items-center gap-1.5 h-7 pr-2 rounded-md text-left text-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40',
            isActive ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
          )}
          style={{ paddingLeft: `${8 + depth * 12}px` }}
        >
          <ChevronRight
            className={cn(
              'w-3 h-3 shrink-0 text-slate-400 transition-transform',
              isExpanded && 'rotate-90',
              !hasChildren && 'invisible',
            )}
            aria-hidden="true"
          />
          <Icon
            className={cn('w-3.5 h-3.5 shrink-0', isActive ? 'text-indigo-600' : 'text-slate-400')}
            style={!isActive && node.color ? { color: node.color } : undefined}
          />
          <span className="truncate">{node.name}</span>
        </button>
        {hasChildren && isExpanded && (
          <ul>{children.map(child => renderNode(child, depth + 1))}</ul>
        )}
      </li>
    );
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-2.5 h-8 px-2.5 rounded-md text-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40',
          isClickUpRoute ? 'text-indigo-700 font-medium' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900',
        )}
      >
        <FolderTree className={cn('w-4 h-4 shrink-0', isClickUpRoute ? 'text-indigo-600' : 'text-slate-500')} aria-hidden="true" />
        <span className="flex-1 truncate text-left">ClickUp</span>
        {loading && (
          <span className="w-3 h-3 rounded-full border border-slate-300 border-t-indigo-600 animate-spin" aria-label="Loading" />
        )}
        <ChevronRight className={cn('w-3.5 h-3.5 text-slate-400 transition-transform', open && 'rotate-90')} aria-hidden="true" />
      </button>

      {open && (
        <div className="mt-0.5 ml-2 border-l border-slate-100 pl-1">
          {tree.length > 0 ? (
            <ul>{tree.map(workspace => renderNode(workspace, 0))}</ul>
          ) : (
            <p className="px-2 py-1.5 text-xs text-slate-500">
              {loading ? 'Loading…' : 'No ClickUp data found'}
            </p>
          )}
          <Link
            to="/admin/clickup-management"
            className={cn(
              'mt-0.5 flex items-center gap-1.5 h-7 px-2 rounded-md text-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40',
              location.pathname.startsWith('/admin/clickup-management')
                ? 'bg-indigo-50 text-indigo-700 font-medium'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
            )}
          >
            <Settings className="w-3.5 h-3.5 shrink-0 text-slate-400" aria-hidden="true" />
            <span>Management</span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default ClickUpNavTree;
